import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { CreateComponent } from './modals/create/create.component';
import { DeleteComponent } from './modals/delete/delete.component';
import { EditComponent } from './modals/edit/edit.component';
import { Upsell } from './models/upsell.model';
import { UpsellService } from './services/upsell.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { AuthStore } from '../../core/stores/auth.store';
import { buildUpsellColumns } from './upsell.columns';

@Component({
  selector: 'app-upsell',
  templateUrl: './upsell.component.html',
  styleUrls: ['./upsell.component.css'],
  imports: [ButtonComponent, GsTableComponent, PaginationComponent],
})
export class UpsellComponent implements OnInit, OnDestroy {
  private dialogService = inject(AppDialogService);
  protected readonly authStore = inject(AuthStore);
  private upsellService = inject(UpsellService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  public data: Array<Upsell>;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  public currentPage: number;
  public refreshing: boolean;
  public subscriptions: Array<Subscription>;
  public refreshPages: boolean = false;
  public columns: ColumnDef<Upsell>[] = [];
  @ViewChild('pagination') pagination: any;

  constructor() {
    this.data = [];
    this.subscriptions = [];
    this.refreshing = false;
    this.totalItemsInDb = 0;
    this.itemsPerPage = 0;
    this.currentPage = this.activatedRoute.snapshot.queryParams['page'];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }
  ngOnInit(): void {
    this.columns = buildUpsellColumns(
      upsell => this.toggleActive(upsell),
      upsell => this.openModalDelete(upsell.id),
      upsell => this.openModalEdit(upsell),
      this.authStore.canEdit()
    );

    let resolved = this.activatedRoute.snapshot.data['api'];
    this.data = resolved.data;
    this.totalItemsInDb = resolved.metadata.total;
    this.itemsPerPage = resolved.metadata.perPage;
    this.currentPage = 1;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      let resolved = this.activatedRoute.snapshot.data['api'];

      if (!params['page'] || params['page'] > resolved.metadata.lastPage) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.data = resolved.data;
        this.totalItemsInDb = resolved.metadata.total;
        this.itemsPerPage = resolved.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });
    this.subscriptions.push(queryparams$);
  }

  public openModalDelete(id) {
    const { closed$ } = this.dialogService.open(DeleteComponent, {
      size: 'md',
      inputs: { id: id },
    });
    this.refreshListOnSuccess(closed$, 1);
  }
  public openModalEdit(item: Upsell) {
    const { closed$ } = this.dialogService.open(EditComponent, {
      size: 'lg',
      inputs: { item: JSON.parse(JSON.stringify(item)) },
    });
    this.refreshListOnSuccess(closed$, this.currentPage);
  }

  public openModalCreate() {
    const { closed$ } = this.dialogService.open(CreateComponent, {
      size: 'lg',
    });
    this.refreshListOnSuccess(closed$, 1);
  }
  private refreshListOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>,
    page?
  ) {
    const onClose$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.refreshing = true;
        this.getall(page);
      }
    });
    this.subscriptions.push(onClose$);
  }

  public toggleActive(item: Upsell) {
    const updated: Upsell = { ...item, active: !item.active };
    this.data = this.data.map(upsell =>
      upsell.id === item.id ? updated : upsell
    );
    const update$ = this.upsellService
      .update$(updated, null, [])
      .subscribe(res => {
        console.log(res);
      });
    this.subscriptions.push(update$);
  }

  private getall(page?) {
    if (!page) page = this.currentPage;

    const all$ = this.upsellService.getAll$(this.currentPage).subscribe({
      next: (res: any) => {
        this.refreshing = false;
        this.data = res.data;
        this.totalItemsInDb = res.metadata.total;
        this.itemsPerPage = res.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
        if (this.currentPage > res.metadata.lastPage) {
          this.currentPage = page;
        }
        this.router.navigate(['/private/upsell'], {
          queryParams: { page: this.currentPage },
        });
      },
      error: err => {
        console.log(err);
      },
    });
    this.subscriptions.push(all$);
  }
  public pageChanged($event) {
    this.router.navigate(['/private/upsell'], {
      queryParams: { page: $event.page },
    });
  }
}
